import { API_BASE_URL } from "@/config/api";
import { ApiError } from "@/services/types";
import { emitUnauthorized } from "@/lib/authEvents";
import { emitUpgradeRequired, PLAN_GATE_ERROR_CODES } from "@/lib/entitlementEvents";
import { getBrowserTimezone } from "@/shared/time/timezone";
import { authDebug } from "@/lib/authDebug";
import { trackedFetch, type LoadingType } from "@/lib/networkActivity";

function loaderModeForPath(path: string) {
  if (path === "/api/me" || path.startsWith("/auth/") || path.startsWith("/integrations/")) {
    return "immediate" as const;
  }
  return "default" as const;
}

let inMemoryAccessToken: string | null = null;
let refreshPromise: Promise<boolean> | null = null;
const ACCESS_TOKEN_STORAGE_KEY = "auth-access-token";

function safeWindow() {
  return typeof window !== "undefined" ? window : undefined;
}

function parseBody(rawText: string) {
  if (!rawText) return null;
  try {
    return JSON.parse(rawText);
  } catch {
    return rawText;
  }
}

export function getAccessToken() {
  return inMemoryAccessToken;
}

export function setAccessToken(token: string) {
  inMemoryAccessToken = token;
  const w = safeWindow();
  if (w) {
    w.localStorage.setItem(ACCESS_TOKEN_STORAGE_KEY, token);
  }
}

export function clearAccessToken() {
  inMemoryAccessToken = null;
  const w = safeWindow();
  if (w) {
    w.localStorage.removeItem(ACCESS_TOKEN_STORAGE_KEY);
  }
}

export function hydrateAccessTokenFromStorage() {
  const w = safeWindow();
  if (!w) return null;
  const token = w.localStorage.getItem(ACCESS_TOKEN_STORAGE_KEY);
  if (token) {
    inMemoryAccessToken = token;
    authDebug("restored access token from storage");
    return token;
  }
  authDebug("no persisted access token found");
  return null;
}

async function runSilentRefresh(loadingType: LoadingType = "background") {
  if (!refreshPromise) {
    refreshPromise = (async () => {
      authDebug("attempting silent refresh");
      try {
        const response = await trackedFetch(`${API_BASE_URL}/auth/refresh`, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: "{}",
          loaderMode: "immediate",
          loadingType,
        });
        if (!response.ok) {
          authDebug("silent refresh rejected", { status: response.status });
          return false;
        }

        const rawText = await response.text();
        const body = parseBody(rawText) as
          | {
              success?: boolean;
              data?: { accessToken?: string };
              accessToken?: string;
            }
          | string
          | null;

        if (body && typeof body === "object") {
          if (body.success === false) return false;
          const token = body.data?.accessToken ?? body.accessToken;
          if (token) setAccessToken(token);
        }

        authDebug("silent refresh succeeded");
        return true;
      } catch {
        authDebug("silent refresh failed due to network/runtime error");
        return false;
      } finally {
        refreshPromise = null;
      }
    })();
  }
  return refreshPromise;
}

export async function authenticatedApiClient<T = unknown>(
  path: string,
  options: RequestInit & { skipGlobalLoader?: boolean; loadingType?: LoadingType } = {},
  retry = true,
): Promise<T> {
  const method = (options.method ?? "GET").toUpperCase();
  const token = getAccessToken();
  const {
    skipGlobalLoader = false,
    loadingType = method === "GET" ? "background" : "user-blocking",
    ...requestOptions
  } = options;
  const headers = new Headers(requestOptions.headers);
  const isFormDataBody = typeof FormData !== "undefined" && requestOptions.body instanceof FormData;

  if (!headers.has("Content-Type") && requestOptions.body && !isFormDataBody) {
    headers.set("Content-Type", "application/json");
  }
  if (token && !headers.has("Authorization")) {
    headers.set("Authorization", `Bearer ${token}`);
  }
  if (!headers.has("X-Timezone")) {
    headers.set("X-Timezone", getBrowserTimezone());
  }
  authDebug("api request", {
    path,
    method,
    retry,
    hasAuthorization: headers.has("Authorization"),
    credentials: "include",
  });

  const response = await trackedFetch(`${API_BASE_URL}${path}`, {
    ...requestOptions,
    credentials: "include",
    headers,
    loaderMode: loaderModeForPath(path),
    loadingType,
    skipGlobalLoader,
  });

  const rawText = await response.text();
  const body = parseBody(rawText);
  authDebug("api response", { path, method, status: response.status, ok: response.ok, retry });

  if (!response.ok) {
    if (response.status === 401) {
      authDebug("api 401 encountered", { path, method, retry });
      const refreshed = retry ? await runSilentRefresh(loadingType) : false;
      if (refreshed) return authenticatedApiClient<T>(path, options, false);

      authDebug("api unauthorized after refresh fallback", { path, method });
      clearAccessToken();
      if (safeWindow()) emitUnauthorized();
      throw new ApiError("UNAUTHORIZED", "Your session has expired. Please sign in again.");
    }

    const errorBody =
      body && typeof body === "object" && "error" in body
        ? (body as { error?: { code?: string; message?: string } }).error
        : undefined;
    const code = typeof errorBody?.code === "string" && errorBody.code.trim() ? errorBody.code.trim() : "HTTP_ERROR";
    const message = typeof errorBody?.message === "string" && errorBody.message.trim()
      ? errorBody.message.trim()
      : `API error: ${response.status}`;

    // Safety net: if a gated action wasn't pre-empted client-side, a plan-limit 403 still
    // opens the shared upgrade prompt globally (the throw also lets callers handle it).
    if (PLAN_GATE_ERROR_CODES.has(code)) {
      emitUpgradeRequired({ code });
    }

    throw new ApiError(code, message);
  }

  return body as T;
}
